import React from 'react';
import moment from 'moment';
import takeRight from 'lodash/takeRight';
import { Box, Dialog, DialogContent, Fade, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import { Meeting, Rivalry, initialsOf } from '../data/rival_insights';
import { formatHistoryDate } from '../data/formatting';
import { TOURNAMENT_ICON_BORDER, playerIconBorderUrl, playerIconUrl } from '../data/player_icons';
import { DS, Overline, enterSx, formatSigned, numericSx, trendColor } from './Dashboard';

const RESULT_META = {
    w: { label: 'W', text: 'Win', color: DS.gain },
    t: { label: 'T', text: 'Tie', color: DS.neutral },
    l: { label: 'L', text: 'Loss', color: DS.loss },
};

export const RecordBar: React.FC<{ w: number; t: number; l: number; height?: number }> = ({ w, t, l, height = 6 }) => {
    const total = w + t + l || 1;
    return (
        <Box sx={{ display: 'flex', height, gap: '2px', borderRadius: '999px', overflow: 'hidden', background: DS.inset }}>
            {w > 0 && <Box sx={{ width: `${(w / total) * 100}%`, background: DS.gain }} />}
            {t > 0 && <Box sx={{ width: `${(t / total) * 100}%`, background: DS.neutral }} />}
            {l > 0 && <Box sx={{ width: `${(l / total) * 100}%`, background: DS.loss }} />}
        </Box>
    );
};

export const RecordText: React.FC<{ w: number; t: number; l: number; size?: number }> = ({ w, t, l, size = 14 }) => (
    <Box component="span" sx={{ fontSize: size, fontWeight: 800, ...numericSx }}>
        <span style={{ color: DS.gain }}>{w}</span>
        <span style={{ color: DS.textFaint }}>–</span>
        <span style={{ color: DS.textMuted }}>{t}</span>
        <span style={{ color: DS.textFaint }}>–</span>
        <span style={{ color: DS.loss }}>{l}</span>
    </Box>
);

export const ResultChips: React.FC<{ meetings: Meeting[]; count?: number }> = ({ meetings, count = 5 }) => (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
        {takeRight(meetings, count).map(m => {
            const meta = RESULT_META[m.result];
            return (
                <Tooltip key={m.timestamp} arrow title={`${meta.text} · ${m.myScore} vs ${m.theirScore} · ${formatHistoryDate(m.timestamp)}`}>
                    <Box sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        fontWeight: 800,
                        color: meta.color,
                        background: alpha(meta.color, 0.16),
                    }}>
                        {meta.label}
                    </Box>
                </Tooltip>
            );
        })}
    </Box>
);

interface AvatarProps {
    name: string;
    color: string;
    size?: number;
    icon?: number;
    iconBorder?: number;
}

/** The player's in-game icon and border, or their initials when we have no icon. */
export const Avatar: React.FC<AvatarProps> = ({ name, color, size = 44, icon, iconBorder }) => {
    const iconUrl = playerIconUrl(icon);
    const [failedUrl, setFailedUrl] = React.useState<string>();
    const borderUrl = playerIconBorderUrl(iconBorder);

    if (iconUrl && iconUrl !== failedUrl) {
        // The game draws the border at 96px around a 74px icon, so it reaches past the icon's box.
        const borderSize = Math.round(size * 96 / 74);
        return (
            <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
                <Box
                    component="img"
                    src={iconUrl}
                    alt=""
                    loading="lazy"
                    onError={() => setFailedUrl(iconUrl)}
                    sx={{ width: size, height: size, display: 'block' }}
                />
                {borderUrl && (
                    <Box
                        component="img"
                        src={borderUrl}
                        alt=""
                        loading="lazy"
                        sx={{
                            position: 'absolute',
                            width: borderSize,
                            height: borderSize,
                            top: (size - borderSize) / 2,
                            left: (size - borderSize) / 2,
                            pointerEvents: 'none',
                            filter: iconBorder === TOURNAMENT_ICON_BORDER ? `drop-shadow(0 0 ${Math.max(2, size * 0.08)}px rgba(255, 214, 102, 0.55))` : undefined,
                        }}
                    />
                )}
            </Box>
        );
    }

    return (
        <Box sx={{
            width: size,
            height: size,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: size * 0.36,
            color,
            background: alpha(color, 0.16),
            border: `1px solid ${alpha(color, 0.4)}`,
        }}>
            {initialsOf(name)}
        </Box>
    );
};

/*****************************************************************************
 ** Versus card for nemesis / favourite / rival
*****************************************************************************/

interface VersusCardProps {
    overline: string;
    caption: string;
    accent: string;
    rivalry?: Rivalry;
    emptyText: string;
    onOpen: (rivalry: Rivalry) => void;
    delay?: number;
}

export const VersusCard: React.FC<VersusCardProps> = ({ overline, caption, accent, rivalry, emptyText, onOpen, delay = 0 }) => (
    <Grid item xs={12} md={4}>
        <Box
            role={rivalry ? 'button' : undefined}
            tabIndex={rivalry ? 0 : undefined}
            onClick={() => rivalry && onOpen(rivalry)}
            onKeyDown={e => { if (rivalry && (e.key === 'Enter' || e.key === ' ')) onOpen(rivalry); }}
            sx={{
                height: '100%',
                p: { xs: 2, sm: 2.5 },
                borderRadius: DS.radius,
                background: `linear-gradient(180deg, ${alpha(accent, 0.12)} 0%, transparent 65%), ${DS.surface}`,
                border: `1px solid ${alpha(accent, 0.3)}`,
                cursor: rivalry ? 'pointer' : 'default',
                transition: 'border-color 0.15s',
                '&:hover': rivalry ? { borderColor: accent } : {},
                ...enterSx(delay),
            }}
        >
            <Overline color={accent}>{overline}</Overline>
            <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{caption}</Typography>

            {rivalry ? (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}>
                        <Avatar name={rivalry.name} color={accent} icon={rivalry.icon} iconBorder={rivalry.iconBorder} />
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: DS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {rivalry.name}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>
                                {rivalry.played} rounds · last {moment(rivalry.lastPlayed).fromNow()}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mt: 2, mb: 0.75 }}>
                        <RecordText w={rivalry.w} t={rivalry.t} l={rivalry.l} size={26} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, ...numericSx }}>{rivalry.winPercent}% wins</Typography>
                    </Box>
                    <RecordBar w={rivalry.w} t={rivalry.t} l={rivalry.l} />

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
                        <ResultChips meetings={rivalry.meetings} />
                        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: trendColor(rivalry.strokesPerRound), ...numericSx }}>
                            {formatSigned(rivalry.strokesPerRound, 1)} strokes / round
                        </Typography>
                    </Box>
                </>
            ) : (
                <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 2 }}>{emptyText}</Typography>
            )}
        </Box>
    </Grid>
);

/*****************************************************************************
 ** Head to head detail dialog
*****************************************************************************/

export const OpponentDialog: React.FC<{ rivalry: Rivalry | null; onClose: () => void }> = ({ rivalry, onClose }) => {
    if (!rivalry) return null;
    const meetings = [...rivalry.meetings].reverse();
    const best = Math.max(...rivalry.meetings.map(m => m.strokeDiff));

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 250 }}
            PaperProps={{
                sx: {
                    background: DS.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${DS.border}`,
                    borderRadius: DS.radius,
                    m: { xs: 1.5, sm: 4 },
                    width: { xs: 'calc(100% - 24px)', sm: undefined },
                },
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2.5, borderBottom: `1px solid ${DS.border}`, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Avatar name={rivalry.name} color={DS.info} size={52} icon={rivalry.icon} iconBorder={rivalry.iconBorder} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Overline>Head to head</Overline>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, letterSpacing: '-0.02em' }}>{rivalry.name}</Typography>
                        <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                            {rivalry.played} rounds together · last {moment(rivalry.lastPlayed).fromNow()}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} aria-label="Close" sx={{ color: DS.textMuted, '&:hover': { color: DS.text } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
                        {[
                            { label: 'Record', value: <RecordText w={rivalry.w} t={rivalry.t} l={rivalry.l} size={22} /> },
                            { label: 'Win rate', value: `${rivalry.winPercent}%` },
                            { label: 'Strokes / round', value: <span style={{ color: trendColor(rivalry.strokesPerRound) }}>{formatSigned(rivalry.strokesPerRound, 1)}</span> },
                            { label: 'Biggest win', value: best > 0 ? `${best} strokes` : '—' },
                        ].map(tile => (
                            <Box key={tile.label} sx={{ p: 1.75, borderRadius: '12px', background: DS.inset, border: `1px solid ${DS.border}` }}>
                                <Overline>{tile.label}</Overline>
                                <Typography component="div" sx={{ fontSize: '22px', fontWeight: 800, color: DS.text, mt: 0.25, ...numericSx }}>{tile.value}</Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <RecordBar w={rivalry.w} t={rivalry.t} l={rivalry.l} height={8} />
                    </Box>

                    <Typography sx={{ fontSize: '13px', fontWeight: 700, color: DS.text, mt: 3, mb: 1 }}>Every round together</Typography>
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                        {meetings.map(m => {
                            const meta = RESULT_META[m.result];
                            return (
                                <Box key={m.timestamp} sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '28px 1fr auto auto',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 1,
                                    borderTop: `1px solid ${DS.border}`,
                                }}>
                                    <Box sx={{
                                        width: 24, height: 24, borderRadius: '6px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '11px', fontWeight: 800, color: meta.color, background: alpha(meta.color, 0.16),
                                    }}>
                                        {meta.label}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '13px', color: DS.text, ...numericSx }}>{formatHistoryDate(m.timestamp)}</Typography>
                                        <Typography sx={{ fontSize: '11px', color: DS.textFaint }}>{m.cardSize}-player card</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '13px', color: DS.textMuted, ...numericSx }}>
                                        <strong style={{ color: DS.text }}>{m.myScore}</strong> vs {m.theirScore}
                                    </Typography>
                                    <Typography sx={{ fontSize: '13px', fontWeight: 700, width: 36, textAlign: 'right', color: trendColor(m.strokeDiff), ...numericSx }}>
                                        {formatSigned(m.strokeDiff)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
